import { useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useVisibleReviews } from "@/lib/firestoreService";

// Shown until real customer reviews exist, so the section isn't empty.
const FALLBACK_REVIEWS = [
  { quote: "The taste reminds me of my grandmother's homemade pickles. Truly authentic Andhra flavor.", customer_name: "Lakshmi P.", rating: 5 },
  { quote: "Best chicken pickle I've tasted in the USA. It brings back so many memories of home.", customer_name: "Ramesh K.", rating: 5 },
  { quote: "Authentic Andhra flavor and excellent packaging. The ulli avakaye is unmatched.", customer_name: "Padma S.", rating: 5 },
  { quote: "I have tried many brands. Annapurna is the only one that tastes truly homemade.", customer_name: "Suresh R.", rating: 5 },
];

const PAGE_SIZE = 4;

export const Testimonials = () => {
  const { t } = useLang();
  const liveReviews = useVisibleReviews();
  const [page, setPage] = useState(0);

  const allReviews = liveReviews && liveReviews.length > 0
    ? liveReviews.map((r) => ({ quote: r.feedback, customer_name: r.customer_name, rating: r.rating }))
    : FALLBACK_REVIEWS;

  const totalPages = Math.max(1, Math.ceil(allReviews.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const reviews = allReviews.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const canPage = allReviews.length > PAGE_SIZE;

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-8 sm:py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="brand-ribbon mb-2">{t.testimonials.ribbon}</div>
        <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-[#5A2D1D]">
          {t.testimonials.h1} <span className="italic text-[#7A3B25]">{t.testimonials.h2}</span>
        </h2>

        <div className="mt-6 flex items-center gap-3">
          {canPage && (
            <button
              onClick={() => setPage((p) => (p - 1 + totalPages) % totalPages)}
              aria-label="Previous reviews"
              className="hidden sm:flex flex-shrink-0 w-9 h-9 rounded-full border border-[#5A2D1D]/30 hover:border-[#5A2D1D] text-[#5A2D1D] items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="testimonials-grid">
            {reviews.map((r, i) => (
              <div
                key={i}
                data-testid="testimonial-card"
                className="relative bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-6 shadow-sm text-left flex flex-col"
              >
                <Quote className="w-6 h-6 text-[#556B2F]/40 mb-3" />
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Star
                      key={idx}
                      className={`w-4 h-4 ${idx < (r.rating || 0) ? "fill-[#556B2F] text-[#556B2F]" : "text-[#5A2D1D]/15"}`}
                    />
                  ))}
                </div>
                <p className="text-sm text-[#2E1A12] leading-relaxed flex-1">
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="mt-4 font-bold text-sm text-[#5A2D1D]">{r.customer_name}</div>
              </div>
            ))}
          </div>

          {canPage && (
            <button
              onClick={() => setPage((p) => (p + 1) % totalPages)}
              aria-label="Next reviews"
              className="hidden sm:flex flex-shrink-0 w-9 h-9 rounded-full border border-[#5A2D1D]/30 hover:border-[#5A2D1D] text-[#5A2D1D] items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {canPage && (
          <div className="flex sm:hidden items-center justify-center gap-4 mt-4">
            <button onClick={() => setPage((p) => (p - 1 + totalPages) % totalPages)} aria-label="Previous reviews" className="w-9 h-9 rounded-full border border-[#5A2D1D]/30 text-[#5A2D1D] grid place-items-center">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-[#2E1A12]/50">{safePage + 1} / {totalPages}</span>
            <button onClick={() => setPage((p) => (p + 1) % totalPages)} aria-label="Next reviews" className="w-9 h-9 rounded-full border border-[#5A2D1D]/30 text-[#5A2D1D] grid place-items-center">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
